import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { renewSession } from '../../services/session';
import { rememberReturnPath, takeReturnPath } from '../../services/returnPath';

const AuthContext = createContext(null);
const AUTH_RETRY_DELAY_MS = 5000;
const AUTH_REQUEST_TIMEOUT_MS = 10000;
const RENEW_CHECK_MS = 30 * 60 * 1000;

const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const apiBaseUrl = process.env.NODE_ENV === 'development' ? baseUrl : `${baseUrl}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authUnavailable, setAuthUnavailable] = useState(false);
  const loginRequestRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;
    let requestController = null;

    const checkAuth = async () => {
      let token = localStorage.getItem('auth_token');
      // Développement local : session ouverte sans Google (backend avec DEV_LOGIN_EMAIL).
      if (!token && process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEV_LOGIN === 'true') {
        try {
          const response = await fetch(`${apiBaseUrl}/auth/dev-login`, { method: 'POST' });
          if (response.ok) {
            token = (await response.json()).token;
            localStorage.setItem('auth_token', token);
          }
        } catch (error) {
          console.warn('Connexion de développement indisponible:', error);
        }
        if (cancelled) return;
      }
      if (!token) {
        if (!cancelled) {
          setAuthUnavailable(false);
          setLoading(false);
        }
        return;
      }

      const controller = new AbortController();
      requestController = controller;
      const timeoutId = window.setTimeout(
        () => controller.abort(),
        AUTH_REQUEST_TIMEOUT_MS,
      );

      try {
        const response = await fetch(`${apiBaseUrl}/auth/verify`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          signal: controller.signal,
        });

        if (cancelled) return;

        if (response.ok) {
          const userData = await response.json();
          if (cancelled) return;
          setUser(userData);
          setAuthUnavailable(false);
          setLoading(false);
        } else if (response.status === 401) {
          localStorage.removeItem('auth_token');
          setUser(null);
          setAuthUnavailable(false);
          setLoading(false);
          rememberReturnPath();
          navigate('/login');
        } else {
          throw new Error(`Vérification de session indisponible (${response.status})`);
        }
      } catch (error) {
        if (cancelled) return;
        console.warn('Vérification de session temporairement indisponible:', error);
        setAuthUnavailable(true);
        setLoading(true);
        retryTimer = window.setTimeout(checkAuth, AUTH_RETRY_DELAY_MS);
      } finally {
        window.clearTimeout(timeoutId);
      }
    };

    checkAuth();

    return () => {
      cancelled = true;
      requestController?.abort();
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [navigate]);

  // Session prolongée chaque jour tant que l'application est ouverte (voir services/session.js).
  const signedIn = Boolean(user);
  useEffect(() => {
    if (!signedIn) return undefined;
    renewSession();
    const timer = window.setInterval(renewSession, RENEW_CHECK_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') renewSession(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [signedIn]);

  const login = useCallback((credential) => {
    if (loginRequestRef.current) {
      return loginRequestRef.current;
    }

    const request = (async () => {
      const response = await fetch(`${apiBaseUrl}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token_id: credential }),
      });

      if (!response.ok) {
        let detail = `Connexion refusée (${response.status})`;
        try {
          const errorData = await response.json();
          detail = errorData.detail || detail;
        } catch (error) {
          // La réponse d'erreur n'est pas nécessairement du JSON.
        }
        throw new Error(detail);
      }

      const data = await response.json();
      localStorage.setItem('auth_token', data.token);
      setUser(data.user);
      navigate(takeReturnPath() || '/');
      return data;
    })();

    const trackedRequest = request.finally(() => {
      if (loginRequestRef.current === trackedRequest) {
        loginRequestRef.current = null;
      }
    });
    loginRequestRef.current = trackedRequest;
    return trackedRequest;
  }, [navigate]);

  const logout = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        await fetch(`${apiBaseUrl}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      setUser(null);
      navigate('/login');
    }
  }, [navigate]);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }, []);

  const updateUser = useCallback((updates) => {
    setUser((current) => (current ? { ...current, ...updates } : current));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
          {authUnavailable && (
            <p className="text-sm text-gray-600" role="status">
              Le serveur est temporairement indisponible. Nouvelle tentative en cours...
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, getAuthHeaders, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user) {
    window.location.href = '/login';
    return null;
  }

  return children;
};
